import React, { useState, useEffect, useRef } from 'react'
import './SupportMain.css'
import TeamHeader from '../TeamHeader/TeamHeader'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import TelegramConnect from './TelegramConnect'

// Auth
import { useAuth } from '../../Auth';

// Icons
import { FaBug } from "react-icons/fa";
import { PiShootingStarBold } from "react-icons/pi";
import { FaAngleDoubleUp } from "react-icons/fa";
import { GrAnnounce } from "react-icons/gr";
import { FaQuestionCircle } from "react-icons/fa";
import { FaRegCircle } from "react-icons/fa";
import { FiEdit2, FiTrash2, FiPlus, FiX, FiSave, FiBell, FiRotateCcw, FiCheck, FiCopy } from "react-icons/fi";

// ─── API Helpers ──────────────────────────────────────────────────────────────

const LOCAL_MODE = false;

function apiUrl(path: string): string {
    return LOCAL_MODE ? `http://localhost:8000${path}` : path;
}

function credentials(): RequestCredentials {
    return LOCAL_MODE ? "include" : "same-origin";
}

const fetchCsrfToken = async () => {
    const response = await fetch(apiUrl('/api/csrf-token/'), {
        method: 'GET',
        credentials: credentials(),
    });
    if (response.ok) {
        const data = await response.json();
        return data.csrfToken;
    }
    return null;
};

function formatUpdateContent(content: string) {
    let formatted = content

    formatted = formatted.replace(/t\[(.*?)\]\n/g, '<div class="update-title">$1</div>')
    formatted = formatted.replace(/b\[(.*?)\]/g, '<strong>$1</strong>')
    formatted = formatted.replace(/u\[(.*?)\]/g, '<span class="update-underline">$1</span>')
    formatted = formatted.replace(/\[(.*?)\]\*\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="update-link">$1</a>')
    formatted = formatted.replace(/\n/g, '<br />')

    return formatted
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Attachment {
    attachment_id: number
    file_url: string
}

interface Ticket {
    ticket_id: number
    title: string
    description: string
    ticket_type: string
    ticket_topic: string
    status: string
    priority: string
    department: string | null                 // submitter's own department
    target_department: number | null           // department that should resolve it
    target_department_name: string | null
    target_department_has_leader: boolean
    created_by: number
    created_by_name: string
    assigned_to: number | null
    assigned_to_name: string | null
    resolution_notes: string | null
    created_at: string
    resolved_at: string | null
    attachments: Attachment[]
    members_can_assign: boolean
}

interface TeamUpdate {
    update_id: number
    title: string
    content: string
    update_type: string
    is_general: boolean
    target_department: string | null
    pinned: boolean
    created_at: string
}

interface DepartmentOption {
    id: number
    name: string
    leader: number | null
    leader_name: string | null
}

interface FnnUser {
    id: number
    user_id: number
    username: string
    email: string
    first_name: string
    last_name: string
}

interface TicketMeta {
    is_manager: boolean
    my_department: string | null
    led_departments: string[]
}

const TICKET_TYPES = [
    'Bug',
    'Feature',
    'Improvement',
    'Maintenance',
    'Request',
    'Documentation',
    'Question',
    'Other'
]
const TICKET_TOPICS = [
    'Website',
    'Customer Portal',
    'Team Portal',
    'Backend',
    'Server',
    'Detector',
    'Esri Dashboards',
    'Fire Analysis',
    'Marketing / PR',
    'New Subscription',
    'Sales',
    'HR'
]

const STATUSES = ['Submitted', 'In Progress', 'Resolved', 'Closed']
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical', 'Emergency']
const UPDATE_TYPES = ['Bug Fix', 'New Feature', 'Adjustment', 'Announcement']

const STATUS_COLORS: Record<string, string> = {
    'Submitted': 'status-submitted',
    'In Progress': 'status-inprogress',
    'Resolved': 'status-resolved',
    'Closed': 'status-closed',
}

const PRIORITY_COLORS: Record<string, string> = {
    'Low': 'priority-low',
    'Medium': 'priority-medium',
    'High': 'priority-high',
    'Critical': 'priority-critical',
    'Emergency': 'priority-emergency',
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
    'Bug Fix': <FaBug />,
    'New Feature': <PiShootingStarBold />,
    'Improvement': <FaAngleDoubleUp />,
    'Question': <FaQuestionCircle />,
    'Announcement': <GrAnnounce />,
    'Other': <FaRegCircle />,
    'Adjustment': <FaAngleDoubleUp />,
}

const DEPARTMENTS = [
    'TOA', 'GIS', 'Hardware', 'Manufacturing',
    'Server', 'Website and Integrations', 'AI',
    'Marketing and Public Relations', 'Finance and HR', 'Commercial', 'Other'
]


type TicketsTab = 'submitted' | 'assigned' | 'department' | 'all' | 'new'

type NotifPriority = 'Low' | 'Medium' | 'High' | 'Critical' | 'Emergency'
type NotifPreset = 'standard' | 'emergency'
type NotifEvent = 'submitted' | 'assigned' | 'in_progress' | 'resolved' | 'closed'
type NotifAudience = 'leader' | 'team'
type NotifChannel = 'email' | 'telegram' | 'teams'

interface NotifEventRule {
    audience: NotifAudience
    channels: NotifChannel[]
}

interface NotifPipelineRules {
    allow_member_assignment: boolean
    events: Record<NotifEvent, NotifEventRule>
}

type NotificationSettings = Record<NotifPriority, NotifPipelineRules>

interface NotificationSettingsPayload {
    department: number
    department_name: string
    settings: NotificationSettings
    defaults: NotificationSettings                      // per-priority defaults
    presets: Record<NotifPreset, NotifPipelineRules>    // "start from" templates
}

const NOTIF_PRIORITIES: NotifPriority[] = ['Low', 'Medium', 'High', 'Critical', 'Emergency']

const NOTIF_PRESETS: { key: NotifPreset; label: string; description: string }[] = [
    {
        key: 'standard',
        label: 'Default pipeline',
        description: 'Only the team leader is notified, by email. Only the leader can assign.',
    },
    {
        key: 'emergency',
        label: 'Emergency pipeline',
        description: 'The whole team is notified by email and Telegram. Anyone on the team can assign.',
    },
]

const NOTIF_EVENTS: { key: NotifEvent; label: string; hint: string }[] = [
    { key: 'submitted', label: 'New ticket', hint: 'Submitted, raised to this priority, or re-routed to this team' },
    { key: 'assigned', label: 'Assigned', hint: 'The assignee is always notified on these channels; the audience gets an FYI' },
    { key: 'in_progress', label: 'In Progress', hint: 'The submitter is always emailed as well' },
    { key: 'resolved', label: 'Resolved', hint: 'The submitter is always emailed, with resolution notes' },
    { key: 'closed', label: 'Closed', hint: 'Off by default' },
]

const NOTIF_CHANNELS: { key: NotifChannel; label: string; soon?: boolean }[] = [
    { key: 'email', label: 'Email' },
    { key: 'telegram', label: 'Telegram' },
    { key: 'teams', label: 'MS Teams' },
]

const cloneRules = <T,>(v: T): T => JSON.parse(JSON.stringify(v))

// Order-insensitive comparison, used for the "Custom" marker on each priority tab
function sameRules(a: NotifPipelineRules, b: NotifPipelineRules): boolean {
    if (a.allow_member_assignment !== b.allow_member_assignment) return false
    return NOTIF_EVENTS.every(({ key }) => {
        const x = a.events[key], y = b.events[key]
        return x.audience === y.audience &&
            [...x.channels].sort().join() === [...y.channels].sort().join()
    })
}

// ─── Utility ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    })
}

function timeAgo(iso: string) {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    const hours = Math.floor(mins / 60)
    const days = Math.floor(hours / 24)
    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    if (mins > 0) return `${mins}m ago`
    return 'just now'
}

function nameMatches(candidate: string | null, username: string, first_name: string, last_name: string): boolean {
    if (!candidate) return false
    const name = candidate.toLowerCase().trim()

    if (first_name || last_name) {
        const fullName = `${first_name ?? ''} ${last_name ?? ''}`.toLowerCase().trim()
        if (fullName && name === fullName) return true
    }

    if (username && name === username.toLowerCase().trim()) return true

    return false
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Badge({ label, className }: { label: string; className?: string }) {
    return <span className={`badge ${className ?? ''}`}>{label}</span>
}

// ─── Confirm Delete Modal ──────────────────────────────────────────────────────

function ConfirmDeleteModal({
    message,
    onConfirm,
    onCancel,
}: {
    message: string
    onConfirm: () => void
    onCancel: () => void
}) {
    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-box confirm-modal" onClick={e => e.stopPropagation()}>
                <h3 className="modal-title">Confirm Delete</h3>
                <p className="confirm-message">{message}</p>
                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onCancel}>Cancel</button>
                    <button className="btn-danger" onClick={onConfirm}>Delete</button>
                </div>
            </div>
        </div>
    )
}

// ─── Ticket Row ───────────────────────────────────────────────────────────────

function TicketRow({
    ticket,
    onClick,
    isAdmin,
    ledDepartments,
    myDepartment,
    username,
    first_name,
    last_name,
    onEdit,
    onDelete,
}: {
    ticket: Ticket
    onClick: () => void
    isAdmin: boolean
    ledDepartments: string[]
    myDepartment: string | null
    username: string
    first_name: string
    last_name: string
    onEdit: (t: Ticket) => void
    onDelete: (t: Ticket) => void
}) {
    const isOwner = nameMatches(ticket.created_by_name, username, first_name, last_name)
    const isAssignee = nameMatches(ticket.assigned_to_name, username, first_name, last_name)
    const isLeader = !!ticket.target_department_name && ledDepartments.includes(ticket.target_department_name)
    // Plain department membership (not leader) — only grants edit access on
    // Emergency tickets, where anyone on the team can claim/assign it.
    const isDepartmentMember = !!ticket.target_department_name && ticket.target_department_name === myDepartment

    const canEdit =
        isAdmin ||
        isLeader ||
        isAssignee ||
        (isOwner && ticket.status === 'Submitted') ||
        (ticket.members_can_assign && isDepartmentMember)

    return (
        <div className="ticket-row" onClick={onClick}>
            <div className="ticket-row-left">
                <span className="ticket-id">#{ticket.ticket_id}</span>
                <div className="ticket-row-meta">
                    <span className="ticket-title">{ticket.title}</span>
                    <div className="ticket-row-tags">
                        <Badge label={ticket.ticket_type} className="badge-type" />
                        <Badge label={ticket.ticket_topic} className="badge-type" />
                        {ticket.target_department_name && (
                            <Badge label={`→ ${ticket.target_department_name} TEAM`} className="badge-dept" />
                        )}
                        {ticket.target_department_name && !ticket.target_department_has_leader && (
                            <Badge label="No Leader" className="badge-warning" />
                        )}
                        {ticket.assigned_to_name && (
                            <Badge label={`→ ${ticket.assigned_to_name}`} className="badge-warning" />
                        )}
                    </div>
                </div>
            </div>

            <div className="ticket-row-right">
                <Badge label={ticket.priority} className={PRIORITY_COLORS[ticket.priority]} />
                <Badge label={ticket.status} className={STATUS_COLORS[ticket.status]} />
                <span className="ticket-time">{timeAgo(ticket.created_at)}</span>

                <div className="row-actions" onClick={e => e.stopPropagation()}>
                    {canEdit && (
                        <button
                            className="row-action-btn edit-btn"
                            title="Edit ticket"
                            onClick={() => onEdit(ticket)}
                        >
                            <FiEdit2 size={13} />
                        </button>
                    )}
                    {isAdmin && (
                        <button
                            className="row-action-btn delete-btn"
                            title="Delete ticket"
                            onClick={() => onDelete(ticket)}
                        >
                            <FiTrash2 size={13} />
                        </button>
                    )}
                </div>

                <span className="ticket-chevron">›</span>
            </div>
        </div>
    )
}

// ─── Ticket Edit Modal ────────────────────────────────────────────────────────

function TicketEditModal({
    ticket,
    isAdmin,
    isLeader,
    isAssignee,
    isOwner,
    isDepartmentMember,
    departments,
    fnnUsers,
    onClose,
    onSaved,
}: {
    ticket: Ticket
    isAdmin: boolean
    isLeader: boolean
    isAssignee: boolean
    isOwner: boolean
    isDepartmentMember: boolean
    departments: DepartmentOption[]
    fnnUsers: FnnUser[]
    onClose: () => void
    onSaved: () => void
}) {
    // Field-level permission flags — mirrors the backend's allowed_fields logic
    const canEditOwnerFields = isAdmin || (isOwner && ticket.status === 'Submitted')
    // Emergency tickets: any department member can claim/assign, not just the leader
    const canEditAssignment = isAdmin || isLeader || (ticket.members_can_assign && isDepartmentMember)
    const canEditStatus = isAdmin || isAssignee
    const canEditResolutionNotes = isAdmin || isAssignee

    const [form, setForm] = useState({
        title: ticket.title,
        description: ticket.description,
        ticket_type: ticket.ticket_type,
        ticket_topic: ticket.ticket_topic,
        priority: ticket.priority,
        target_department: ticket.target_department != null ? String(ticket.target_department) : '',
        status: ticket.status,
        assigned_to: ticket.assigned_to != null ? String(ticket.assigned_to) : '',
        resolution_notes: ticket.resolution_notes ?? '',
    })
    const [files, setFiles] = useState<File[]>([])
    const [saving, setSaving] = useState(false)
    const fileRef = useRef<HTMLInputElement>(null)

    // Searchable assignee autocomplete — same pattern as JobApplicants.tsx
    const [assigneeSearch, setAssigneeSearch] = useState(ticket.assigned_to_name ?? '')
    const [assigneeFocused, setAssigneeFocused] = useState(false)

    const findUserByNameOrEmail = (input: string): FnnUser | null => {
        const normalized = input.toLowerCase().trim()
        return fnnUsers.find(u =>
            `${u.first_name} ${u.last_name}`.toLowerCase() === normalized ||
            u.email.toLowerCase() === normalized
        ) || null
    }

    const selectAssignee = (u: FnnUser) => {
        setForm(prev => ({ ...prev, assigned_to: String(u.id) }))
        setAssigneeSearch(`${u.first_name} ${u.last_name}`)
        setAssigneeFocused(false)
    }

    const clearAssignee = () => {
        setForm(prev => ({ ...prev, assigned_to: '' }))
        setAssigneeSearch('')
    }

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) setFiles(Array.from(e.target.files))
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            const fd = new FormData()

            if (canEditOwnerFields) {
                fd.append('title', form.title)
                fd.append('description', form.description)
                fd.append('ticket_type', form.ticket_type)
                fd.append('ticket_topic', form.ticket_topic)
                fd.append('priority', form.priority)
                if (form.target_department) fd.append('target_department', form.target_department)
            }

            if (canEditAssignment) {
                // append even when empty, so clearing the assignee actually unassigns
                fd.append('assigned_to', form.assigned_to)
            }

            if (canEditStatus) {
                fd.append('status', form.status)
            }

            if (canEditResolutionNotes && form.resolution_notes) {
                fd.append('resolution_notes', form.resolution_notes)
            }

            files.forEach(f => fd.append('attachments', f))

            const res = await fetch(apiUrl(`/portal/api/support/tickets/${ticket.ticket_id}/`), {
                method: 'PATCH',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
                body: fd,
            })

            if (res.ok) {
                toast.success('Ticket updated.')
                onSaved()
                onClose()
            } else {
                toast.error('Failed to update ticket.')
            }
        } catch {
            toast.error('Failed to update ticket.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box ticket-edit-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">
                        Edit Ticket <span className="modal-id">#{ticket.ticket_id}</span>
                    </h3>
                    <button className="modal-close-btn" onClick={onClose}><FiX /></button>
                </div>

                <div className="modal-body">

                    {canEditOwnerFields ? (
                        <>
                            <div className="form-field">
                                <label>Title</label>
                                <input name="title" value={form.title} onChange={handleChange} />
                            </div>

                            <div className="form-field">
                                <label>Description</label>
                                <textarea name="description" rows={3} value={form.description} onChange={handleChange} />
                            </div>

                            <div className="form-grid-2">
                                <div className="form-field">
                                    <label>Type</label>
                                    <select name="ticket_type" value={form.ticket_type} onChange={handleChange}>
                                        {TICKET_TYPES.map(t => <option key={t}>{t}</option>)}
                                    </select>
                                </div>

                                <div className="form-field">
                                    <label>Topic</label>
                                    <select name="ticket_topic" value={form.ticket_topic} onChange={handleChange}>
                                        {TICKET_TOPICS.map(t => <option key={t}>{t}</option>)}
                                    </select>
                                </div>

                                <div className="form-field">
                                    <label>Route to Team</label>
                                    <select name="target_department" value={form.target_department} onChange={handleChange}>
                                        <option value="">— Select —</option>
                                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                    </select>
                                </div>

                                <div className="form-field">
                                    <label>Priority</label>
                                    <select name="priority" value={form.priority} onChange={handleChange}>
                                        {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                                    </select>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="form-grid-2">
                            <div className="form-field">
                                <label>Type</label>
                                <span className="readonly-value">{ticket.ticket_type}</span>
                            </div>
                            <div className="form-field">
                                <label>Topic</label>
                                <span className="readonly-value">{ticket.ticket_topic}</span>
                            </div>
                            <div className="form-field">
                                <label>Routed to</label>
                                <span className="readonly-value">
                                    {ticket.target_department_name ?? '— None —'}
                                    {ticket.target_department_name && !ticket.target_department_has_leader && (
                                        <Badge label="No Leader" className="badge-warning" />
                                    )}
                                </span>
                            </div>
                            <div className="form-field">
                                <label>Priority</label>
                                <span className="readonly-value">{ticket.priority}</span>
                            </div>
                        </div>
                    )}

                    {canEditAssignment && (
                        <div className="form-field ja-assignee-item">
                            <label>Assign To <span className="optional">(any FNN member)</span></label>
                            <input
                                type="text"
                                className="ja-assignee-input"
                                value={assigneeSearch}
                                placeholder="Search by name or email…"
                                onFocus={() => setAssigneeFocused(true)}
                                onChange={e => {
                                    setAssigneeSearch(e.target.value)
                                    setAssigneeFocused(true)
                                    // clear the selection if they start editing the text again
                                    setForm(prev => ({ ...prev, assigned_to: '' }))
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        const u = findUserByNameOrEmail(assigneeSearch)
                                        if (u) selectAssignee(u)
                                    } else if (e.key === 'Escape') {
                                        setAssigneeFocused(false)
                                    }
                                }}
                                onBlur={() => setTimeout(() => setAssigneeFocused(false), 150)}
                            />

                            {assigneeFocused && fnnUsers.length > 0 && (
                                <div className="ja-autocomplete-list">
                                    {fnnUsers
                                        .filter(u =>
                                            `${u.first_name} ${u.last_name}`.toLowerCase().includes(assigneeSearch.toLowerCase()) ||
                                            u.email.toLowerCase().includes(assigneeSearch.toLowerCase())
                                        )
                                        .map(u => (
                                            <div
                                                key={u.id}
                                                className="ja-autocomplete-item"
                                                onMouseDown={e => {
                                                    e.preventDefault()
                                                    selectAssignee(u)
                                                }}
                                            >
                                                {u.first_name} {u.last_name} ({u.email})
                                            </div>
                                        ))}
                                </div>
                            )}

                            {form.assigned_to && (
                                <button type="button" className="ja-clear-assignee-btn" onClick={clearAssignee}>
                                    <FiX size={11} /> Clear assignee
                                </button>
                            )}
                        </div>
                    )}

                    {canEditStatus ? (
                        <div className="form-field">
                            <label>Status</label>
                            <select name="status" value={form.status} onChange={handleChange}>
                                {STATUSES.map(s => <option key={s}>{s}</option>)}
                            </select>
                        </div>
                    ) : (
                        <div className="form-field">
                            <label>Status</label>
                            <span className="readonly-value">{ticket.status}</span>
                        </div>
                    )}

                    {canEditResolutionNotes && (
                        <div className="form-field">
                            <label>Resolution Notes <span className="optional">(optional)</span></label>
                            <textarea
                                name="resolution_notes"
                                rows={3}
                                value={form.resolution_notes}
                                onChange={handleChange}
                                placeholder="Add resolution details…"
                            />
                        </div>
                    )}

                    {ticket.attachments.length > 0 && (
                        <div className="form-field">
                            <label>Existing Attachments</label>
                            <div className="attachment-list">
                                {ticket.attachments.map(a => (
                                    <a
                                        key={a.attachment_id}
                                        href={a.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="attachment-chip"
                                    >
                                        <span className="attachment-icon">⊞</span>
                                        File {a.attachment_id}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="form-field">
                        <label>Add Attachments <span className="optional">(optional)</span></label>
                        <div
                            className="file-drop-zone"
                            onClick={() => fileRef.current?.click()}
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files)) }}
                        >
                            {files.length > 0
                                ? <span className="file-list">{files.map(f => f.name).join(', ')}</span>
                                : <span className="file-placeholder">Drop files here or click to browse</span>
                            }
                        </div>
                        <input ref={fileRef} type="file" multiple onChange={handleFiles} style={{ display: 'none' }} />
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        <FiSave size={13} />
                        {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Ticket Detail Panel ───────────────────────────────────────────────────────

function TicketDetailPanel({
    ticket,
    onClose,
}: {
    ticket: Ticket
    onClose: () => void
}) {
    return (
        <div className="detail-panel">
            <div className="detail-panel-header">
                <div>
                    <span className="ticket-id large">#{ticket.ticket_id}</span>
                    <h2 className="detail-title">{ticket.title}</h2>
                </div>
                <button className="close-btn" onClick={onClose}>✕</button>
            </div>

            <div className="detail-badges">
                <Badge label={ticket.ticket_type} className="badge-type" />
                <Badge label={ticket.ticket_topic} className="badge-type" />
                <Badge label={ticket.priority} className={PRIORITY_COLORS[ticket.priority]} />
                <Badge label={ticket.status} className={STATUS_COLORS[ticket.status]} />
                {ticket.target_department_name && (
                    <Badge label={`→ ${ticket.target_department_name}`} className="badge-dept" />
                )}
                {ticket.target_department_name && !ticket.target_department_has_leader && (
                    <Badge label="No Leader Assigned" className="badge-warning" />
                )}
            </div>

            <div className="detail-meta-grid">
                <div className="meta-item">
                    <span className="meta-label">Submitted by</span>
                    <span>{ticket.created_by_name}</span>
                </div>
                {ticket.department && (
                    <div className="meta-item">
                        <span className="meta-label">Submitter&apos;s Team</span>
                        <span>{ticket.department}</span>
                    </div>
                )}
                <div className="meta-item">
                    <span className="meta-label">Created</span>
                    <span>{formatDate(ticket.created_at)}</span>
                </div>
                {ticket.assigned_to_name && (
                    <div className="meta-item">
                        <span className="meta-label">Assigned to</span>
                        <span>{ticket.assigned_to_name}</span>
                    </div>
                )}
                {ticket.resolved_at && (
                    <div className="meta-item">
                        <span className="meta-label">Resolved</span>
                        <span>{formatDate(ticket.resolved_at)}</span>
                    </div>
                )}
            </div>

            <div className="detail-section">
                <span className="detail-section-label">Description</span>
                <p className="detail-description">{ticket.description}</p>
            </div>

            {ticket.resolution_notes && (
                <div className="detail-section resolution-section">
                    <span className="detail-section-label">Resolution Notes</span>
                    <p className="detail-description">{ticket.resolution_notes}</p>
                </div>
            )}

            {ticket.attachments.length > 0 && (
                <div className="detail-section">
                    <span className="detail-section-label">
                        Attachments ({ticket.attachments.length})
                    </span>
                    <div className="attachment-list">
                        {ticket.attachments.map(a => (
                            <a
                                key={a.attachment_id}
                                href={a.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="attachment-chip"
                            >
                                <span className="attachment-icon">⊞</span>
                                File {a.attachment_id}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── New Ticket Form ───────────────────────────────────────────────────────────

function NewTicketForm({ onSuccess, departments }: { onSuccess: () => void; departments: DepartmentOption[] }) {
    const [form, setForm] = useState({
        title: '',
        description: '',
        ticket_type: 'Bug',
        ticket_topic: 'Website',
        priority: 'Medium',
        target_department: '',
    })
    const [files, setFiles] = useState<File[]>([])
    const [submitting, setSubmitting] = useState(false)
    const fileRef = useRef<HTMLInputElement>(null)

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) setFiles(Array.from(e.target.files))
    }

    const handleSubmit = async () => {
        if (!form.title.trim() || !form.description.trim()) {
            toast.error('Title and description are required.')
            return
        }
        if (!form.target_department) {
            toast.error('Please select which department should resolve this ticket.')
            return
        }
        setSubmitting(true)
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('Failed to get CSRF token.'); return }

            const fd = new FormData()
            Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v) })
            files.forEach(f => fd.append('attachments', f))

            const response = await fetch(apiUrl('/portal/api/support/tickets/'), {
                method: 'POST',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
                body: fd,
            })

            if (response.ok) {
                toast.success('Ticket submitted!')
                setForm({ title: '', description: '', ticket_type: 'Bug', ticket_topic: 'Website', priority: 'Medium', target_department: '' })
                setFiles([])
                onSuccess()
            } else {
                toast.error('Failed to submit ticket.')
            }
        } catch {
            toast.error('Failed to submit ticket.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="new-ticket-form-wrapper">
            <div className="new-ticket-form">
                <h3 className="form-heading">Submit a New Ticket</h3>

                <div className="form-grid-2">
                    <div className="form-field">
                        <label>Ticket Type</label>
                        <select name="ticket_type" value={form.ticket_type} onChange={handleChange}>
                            {TICKET_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                    </div>

                    <div className="form-field">
                        <label>Ticket Topic</label>
                        <select name="ticket_topic" value={form.ticket_topic} onChange={handleChange}>
                            {TICKET_TOPICS.map(t => <option key={t}>{t}</option>)}
                        </select>
                    </div>

                    <div className="form-field">
                        <label>Priority</label>
                        <select name="priority" value={form.priority} onChange={handleChange}>
                            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                        </select>
                    </div>

                    <div className="form-field">
                        <label>Route to Team</label>
                        <select name="target_department" value={form.target_department} onChange={handleChange}>
                            <option value="">— Select which team should resolve this —</option>
                            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>
                </div>

                <div className="form-field">
                    <label>Title</label>
                    <input name="title" placeholder="Brief summary of the issue…" value={form.title} onChange={handleChange} />
                </div>

                <div className="form-field">
                    <label>Description</label>
                    <textarea name="description" placeholder="Provide as much detail as possible..." rows={4} value={form.description} onChange={handleChange} />
                </div>

                <div className="form-field">
                    <label>Attachments <span className="optional">(optional)</span></label>
                    <div
                        className="file-drop-zone"
                        onClick={() => fileRef.current?.click()}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files)) }}
                    >
                        {files.length > 0
                            ? <span className="file-list">{files.map(f => f.name).join(', ')}</span>
                            : <span className="file-placeholder">Drop files here or click to browse</span>
                        }
                    </div>
                    <input ref={fileRef} type="file" multiple onChange={handleFiles} style={{ display: 'none' }} />
                </div>

                <button className="spp-submit-btn" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Ticket →'}
                </button>
            </div>
        </div>
    )
}

// ─── Update Card ───────────────────────────────────────────────────────────────

const UPDATE_TYPE_COLORS: Record<string, string> = {
    'Bug Fix': 'upd-bugfix',
    'New Feature': 'upd-feature',
    'Improvement': 'upd-improvement',
    'Announcement': 'upd-announcement',
    'Adjustment': 'upd-maintenance',
}

function UpdateCard({
    update,
    isAdmin,
    onEdit,
    onDelete,
}: {
    update: TeamUpdate
    isAdmin: boolean
    onEdit: (u: TeamUpdate) => void
    onDelete: (u: TeamUpdate) => void
}) {
    const [expanded, setExpanded] = useState(false)

    return (
        <div className={`update-card ${update.pinned ? 'pinned' : ''}`}>
            <div className="update-card-header" onClick={() => setExpanded(e => !e)}>
                <div className="update-card-left">
                    <span className="update-type-icon">
                        {TYPE_ICONS[update.update_type] ?? '•'}
                    </span>
                    <div>
                        <div className="update-card-title">
                            {update.pinned && <span className="pin-icon">📌 </span>}
                            {update.title}
                        </div>
                        <div className="update-card-sub">
                            <Badge label={update.update_type} className={UPDATE_TYPE_COLORS[update.update_type] ?? ''} />
                            <span className="update-time">{timeAgo(update.created_at)}</span>
                        </div>
                    </div>
                </div>

                <div className="update-card-right" onClick={e => e.stopPropagation()}>
                    {isAdmin && (
                        <div className="row-actions">
                            <button
                                className="row-action-btn edit-btn"
                                title="Edit update"
                                onClick={() => onEdit(update)}
                            >
                                <FiEdit2 size={13} />
                            </button>
                            <button
                                className="row-action-btn delete-btn"
                                title="Delete update"
                                onClick={() => onDelete(update)}
                            >
                                <FiTrash2 size={13} />
                            </button>
                        </div>
                    )}
                    <span className={`expand-chevron ${expanded ? 'open' : ''}`}>›</span>
                </div>
            </div>

            {expanded && (
                <div className="update-card-body">
                    <p dangerouslySetInnerHTML={{ __html: formatUpdateContent(update.content) }} />
                    <span className="update-date">
                        {formatDate(update.created_at)}
                        {!update.is_general && update.target_department && (
                            <Badge label={update.target_department} className="badge-dept" />
                        )}
                    </span>
                </div>
            )}
        </div>
    )
}

// ─── Update Edit/Create Modal ──────────────────────────────────────────────────

function UpdateModal({
    update,
    onClose,
    onSaved,
}: {
    update: TeamUpdate | null
    onClose: () => void
    onSaved: () => void
}) {
    const isCreate = update === null
    const [form, setForm] = useState({
        title: update?.title ?? '',
        content: update?.content ?? '',
        update_type: update?.update_type ?? 'Announcement',
        is_general: update?.is_general ?? true,
        target_department: update?.target_department ?? '',
        pinned: update?.pinned ?? false,
    })
    const [saving, setSaving] = useState(false)

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const target = e.target as HTMLInputElement
        const value = target.type === 'checkbox' ? target.checked : target.value
        setForm(prev => ({ ...prev, [target.name]: value }))
    }

    const handleSave = async () => {
        if (!form.title.trim() || !form.content.trim()) {
            toast.error('Title and content are required.')
            return
        }
        setSaving(true)
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            const payload = {
                title: form.title,
                content: form.content,
                update_type: form.update_type,
                is_general: form.is_general,
                target_department: form.is_general ? null : (form.target_department || null),
                pinned: form.pinned,
            }

            const url = isCreate
                ? apiUrl('/portal/api/support/updates/')
                : apiUrl(`/portal/api/support/updates/${update!.update_id}/`)

            const res = await fetch(url, {
                method: isCreate ? 'POST' : 'PATCH',
                credentials: credentials(),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(payload),
            })

            if (res.ok) {
                toast.success(isCreate ? 'Update posted.' : 'Update saved.')
                onSaved()
                onClose()
            } else {
                toast.error('Failed to save update.')
            }
        } catch {
            toast.error('Failed to save update.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box update-edit-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">
                        {isCreate ? 'New Team Update' : `Edit Update #${update!.update_id}`}
                    </h3>
                    <button className="modal-close-btn" onClick={onClose}><FiX /></button>
                </div>

                <div className="modal-body">
                    <div className="form-field">
                        <label>Title</label>
                        <input name="title" value={form.title} onChange={handleChange} placeholder="Update title…" />
                    </div>

                    <div className="form-field">
                        <label>Content</label>
                        <textarea name="content" rows={5} value={form.content} onChange={handleChange} placeholder="Write the update content…" />
                    </div>

                    <div className="form-grid-2">
                        <div className="form-field">
                            <label>Type</label>
                            <select name="update_type" value={form.update_type} onChange={handleChange}>
                                {UPDATE_TYPES.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>

                        <div className="form-field">
                            <label>Audience</label>
                            <select
                                name="is_general"
                                value={form.is_general ? 'true' : 'false'}
                                onChange={e => setForm(prev => ({ ...prev, is_general: e.target.value === 'true' }))}
                            >
                                <option value="true">General (all)</option>
                                <option value="false">Specific department</option>
                            </select>
                        </div>
                    </div>

                    {!form.is_general && (
                        <div className="form-field">
                            <label>Target Team</label>
                            <select name="target_department" value={form.target_department} onChange={handleChange}>
                                <option value="">— Select —</option>
                                {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="form-field spp-checkbox-field">
                        <label className="spp-checkbox-label">
                            <input
                                type="checkbox"
                                name="pinned"
                                checked={form.pinned}
                                onChange={handleChange}
                            />
                            <span className={`lmp-list-check ${form.pinned ? "lmp-checked" : ""}`}>
                                {form.pinned && (
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                        <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </span>
                            <span>Pin this update</span>
                        </label>
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                        <FiSave size={13} />
                        {saving ? 'Saving…' : isCreate ? 'Post Update' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Updates Tab ───────────────────────────────────────────────────────────────

function UpdatesTab({
    updates,
    isAdmin,
    onEdit,
    onDelete,
    onNew,
}: {
    updates: TeamUpdate[]
    isAdmin: boolean
    onEdit: (u: TeamUpdate) => void
    onDelete: (u: TeamUpdate) => void
    onNew: () => void
}) {
    const depts = Array.from(
        new Set(
            updates.filter(u => !u.is_general && u.target_department).map(u => u.target_department as string)
        )
    )
    const tabs = ['General', ...depts]
    const [activeTab, setActiveTab] = useState('General')

    const filtered = updates.filter(u =>
        activeTab === 'General' ? u.is_general : !u.is_general && u.target_department === activeTab
    )

    return (
        <div className="updates-tab">
            <div className="updates-toolbar">
                <div className="dept-tabs">
                    {tabs.map(t => (
                        <button
                            key={t}
                            className={`dept-tab ${activeTab === t ? 'active' : ''}`}
                            onClick={() => setActiveTab(t)}
                        >
                            {t}
                            <span className="dept-tab-count">
                                {updates.filter(u =>
                                    t === 'General' ? u.is_general : (!u.is_general && u.target_department === t)
                                ).length}
                            </span>
                        </button>
                    ))}
                </div>

                {isAdmin && (
                    <button className="new-update-btn" onClick={onNew}>
                        <FiPlus size={14} />
                        New Update
                    </button>
                )}
            </div>

            <div className="updates-list">
                {filtered.length === 0 ? (
                    <div className="empty-state">No updates for this department yet.</div>
                ) : (
                    filtered.map(u => (
                        <UpdateCard key={u.update_id} update={u} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDelete} />
                    ))
                )}
            </div>
        </div>
    )
}

function NotificationSettingsModal({
    departments,
    onClose,
}: {
    departments: DepartmentOption[]     // only the ones this user may manage
    onClose: () => void
}) {
    const [deptId, setDeptId] = useState<number | null>(departments[0]?.id ?? null)
    const [priority, setPriority] = useState<NotifPriority>('Low')
    const [settings, setSettings] = useState<NotificationSettings | null>(null)
    const [defaults, setDefaults] = useState<NotificationSettings | null>(null)
    const [presets, setPresets] = useState<Record<NotifPreset, NotifPipelineRules> | null>(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [dirty, setDirty] = useState(false)

    const applyPayload = (data: NotificationSettingsPayload) => {
        setSettings(data.settings)
        setDefaults(data.defaults)
        setPresets(data.presets)
        setDirty(false)
    }

    const [copySource, setCopySource] = useState<NotifPriority | ''>('')

    // Clear the "copy from" picker when switching priority tabs
    useEffect(() => { setCopySource('') }, [priority])

    useEffect(() => {
        if (deptId == null) return
        let cancelled = false
        const load = async () => {
            setLoading(true)
            try {
                const res = await fetch(apiUrl(`/portal/api/support/departments/${deptId}/notifications/`), {
                    method: 'GET',
                    credentials: credentials(),
                })
                if (!res.ok) throw new Error()
                const data: NotificationSettingsPayload = await res.json()
                if (!cancelled) applyPayload(data)
            } catch {
                if (!cancelled) {
                    setSettings(null)
                    toast.error('Could not load notification settings.')
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        load()
        return () => { cancelled = true }
    }, [deptId])

    const confirmDiscard = () => !dirty || window.confirm('Discard unsaved changes?')

    const handleClose = () => { if (confirmDiscard()) onClose() }

    const changeDepartment = (id: number) => {
        if (id === deptId || !confirmDiscard()) return
        setDeptId(id)
    }

    // Replace the rule set of the priority currently being edited
    const updateRules = (fn: (r: NotifPipelineRules) => NotifPipelineRules) => {
        setSettings(prev => prev && { ...prev, [priority]: fn(prev[priority]) })
        setDirty(true)
    }

    const updateEvent = (event: NotifEvent, patch: Partial<NotifEventRule>) => {
        updateRules(r => ({
            ...r,
            events: { ...r.events, [event]: { ...r.events[event], ...patch } },
        }))
    }

    const toggleChannel = (event: NotifEvent, channel: NotifChannel) => {
        if (!settings) return
        const current = settings[priority].events[event].channels
        const next = current.includes(channel)
            ? current.filter(c => c !== channel)
            : [...current, channel]
        // keep the canonical order (email, telegram, teams)
        updateEvent(event, { channels: NOTIF_CHANNELS.map(c => c.key).filter(k => next.includes(k)) })
    }

    const applyPreset = (preset: NotifPreset) => {
        if (presets) updateRules(() => cloneRules(presets[preset]))
    }

    const copyFrom = (source: NotifPriority) => {
        if (settings) updateRules(() => cloneRules(settings[source]))
    }

    const resetThisPriority = () => {
        if (defaults) updateRules(() => cloneRules(defaults[priority]))
    }

    // Push the current priority's rules onto every other priority except Emergency
    const applyToAllStandard = () => {
        if (!settings) return
        const source = settings[priority]
        setSettings(prev => {
            if (!prev) return prev
            const next = { ...prev }
            NOTIF_PRIORITIES.filter(p => p !== 'Emergency').forEach(p => { next[p] = cloneRules(source) })
            return next
        })
        setDirty(true)
    }

    const handleSave = async () => {
        if (!settings || deptId == null) return
        setSaving(true)
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            const res = await fetch(apiUrl(`/portal/api/support/departments/${deptId}/notifications/`), {
                method: 'PATCH',
                credentials: credentials(),
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
                body: JSON.stringify(settings),
            })
            if (!res.ok) throw new Error()
            applyPayload(await res.json())
            toast.success('Notification settings saved.')
        } catch {
            toast.error('Failed to save notification settings.')
        } finally {
            setSaving(false)
        }
    }

    const handleResetAll = async () => {
        if (deptId == null) return
        if (!window.confirm('Reset every priority for this team to the defaults?')) return
        setSaving(true)
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            const res = await fetch(apiUrl(`/portal/api/support/departments/${deptId}/notifications/`), {
                method: 'DELETE',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
            })
            if (!res.ok) throw new Error()
            applyPayload(await res.json())
            toast.success('Reset to defaults.')
        } catch {
            toast.error('Failed to reset settings.')
        } finally {
            setSaving(false)
        }
    }

    const rules = settings?.[priority]
    const isCustom = (p: NotifPriority) =>
        !!settings && !!defaults && !sameRules(settings[p], defaults[p])

    // Which premade pipeline (if any) the current priority's rules exactly match
    const activePreset: NotifPreset | null =
        rules && presets
            ? NOTIF_PRESETS.find(pr => sameRules(rules, presets[pr.key]))?.key ?? null
            : null

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-box notif-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="modal-title">
                        <FiBell size={14} /> Notification Settings
                    </h3>
                    <button className="modal-close-btn" onClick={handleClose}><FiX /></button>
                </div>

                <div className="modal-body">
                    {departments.length > 1 && (
                        <div className="form-field">
                            <label>Team</label>
                            <select
                                value={deptId ?? ''}
                                onChange={e => changeDepartment(Number(e.target.value))}
                            >
                                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="notif-priority-tabs">
                        {NOTIF_PRIORITIES.map(p => (
                            <button
                                key={p}
                                className={`notif-priority-tab ${priority === p ? 'active' : ''} ${p === 'Emergency' ? 'is-emergency' : ''}`}
                                onClick={() => setPriority(p)}
                            >
                                <Badge label={p} className={PRIORITY_COLORS[p]} />
                                {isCustom(p) && <span className="notif-custom-dot" title="Customised" />}
                            </button>
                        ))}
                    </div>

                    {loading && <div className="loading-state">Loading settings…</div>}

                    {!loading && rules && (
                        <>
                            {/* ── Premade pipelines ─────────────────────────── */}
                            <section className="notif-section">
                                <div className="notif-section-head">
                                    <div>
                                        <h4 className="notif-section-title">Choose a premade pipeline</h4>
                                        <p className="notif-section-hint">
                                            Sets every rule below for <strong>{priority}</strong> tickets in one click.
                                            You can still fine-tune individual events afterwards.
                                        </p>
                                    </div>
                                    {!activePreset && <span className="notif-custom-badge">Custom</span>}
                                </div>

                                <div className="notif-preset-grid">
                                    {NOTIF_PRESETS.map(pr => {
                                        const active = activePreset === pr.key
                                        return (
                                            <button
                                                key={pr.key}
                                                type="button"
                                                className={`notif-preset-card ${active ? 'active' : ''} ${pr.key === 'emergency' ? 'is-emergency' : ''}`}
                                                aria-pressed={active}
                                                onClick={() => applyPreset(pr.key)}
                                            >
                                                <span className="notif-preset-check">
                                                    {active && <FiCheck size={11} />}
                                                </span>
                                                <span className="notif-preset-body">
                                                    <span className="notif-preset-title">{pr.label}</span>
                                                    <span className="notif-preset-desc">{pr.description}</span>
                                                </span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </section>

                            {/* ── Member assignment ─────────────────────────── */}
                            <button
                                type="button"
                                className={`notif-toggle-row ${rules.allow_member_assignment ? 'on' : ''}`}
                                aria-pressed={rules.allow_member_assignment}
                                onClick={() => updateRules(r => ({ ...r, allow_member_assignment: !r.allow_member_assignment }))}
                            >
                                <span className="notif-switch"><span className="notif-switch-knob" /></span>
                                <span className="notif-toggle-text">
                                    <strong>Any team member can assign {priority} tickets</strong>
                                    <span>
                                        {rules.allow_member_assignment
                                            ? 'Team members can claim a ticket or assign it to anyone.'
                                            : 'Only the team leader (or a manager) can assign.'}
                                    </span>
                                </span>
                            </button>

                            {/* ── Per-event rules ───────────────────────────── */}
                            <div className="notif-grid">
                                <div className="notif-grid-head">
                                    <span>Event</span>
                                    <span>Notify</span>
                                    <span>Channels</span>
                                </div>

                                {NOTIF_EVENTS.map(ev => {
                                    const rule = rules.events[ev.key]
                                    const off = rule.channels.length === 0
                                    return (
                                        <div key={ev.key} className={`notif-row ${off ? 'is-off' : ''}`}>
                                            <div className="notif-event">
                                                <span className="notif-event-label">{ev.label}</span>
                                                <span className="notif-event-hint">{ev.hint}</span>
                                            </div>

                                            <div className="notif-segment" role="group">
                                                <button
                                                    className={rule.audience === 'leader' ? 'active' : ''}
                                                    onClick={() => updateEvent(ev.key, { audience: 'leader' })}
                                                >
                                                    Team leader
                                                </button>
                                                <button
                                                    className={rule.audience === 'team' ? 'active' : ''}
                                                    onClick={() => updateEvent(ev.key, { audience: 'team' })}
                                                >
                                                    Whole team
                                                </button>
                                            </div>

                                            <div className="notif-channels">
                                                {NOTIF_CHANNELS.map(ch => (
                                                    <button
                                                        key={ch.key}
                                                        className={`filter-chip ${rule.channels.includes(ch.key) ? 'active' : ''}`}
                                                        aria-pressed={rule.channels.includes(ch.key)}
                                                        title={ch.soon ? 'Saved now, delivered once the MS Teams integration ships' : undefined}
                                                        onClick={() => toggleChannel(ev.key, ch.key)}
                                                    >
                                                        {ch.label}
                                                        {ch.soon && <span className="notif-soon">soon</span>}
                                                    </button>
                                                ))}
                                                {off && <span className="notif-off-label">Off</span>}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            {/* ── Reuse settings ────────────────────────────── */}
                            <section className="notif-section notif-tools">
                                <div className="notif-section-head">
                                    <div>
                                        <h4 className="notif-section-title">Reuse these settings</h4>
                                        <p className="notif-section-hint">
                                            Copy rules between priorities instead of setting each one up by hand.
                                        </p>
                                    </div>
                                </div>

                                <div className="notif-tool-row">
                                    <div className="notif-tool-text">
                                        <strong>Copy from another priority</strong>
                                        <span>Replace the {priority} rules with the ones from the priority you pick.</span>
                                    </div>
                                    <div className="notif-tool-actions">
                                        <select
                                            className="notif-copy-select"
                                            value={copySource}
                                            onChange={e => setCopySource(e.target.value as NotifPriority | '')}
                                        >
                                            <option value="">Select priority…</option>
                                            {NOTIF_PRIORITIES.filter(p => p !== priority).map(p => (
                                                <option key={p} value={p}>{p}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className="btn-ghost btn-sm"
                                            disabled={!copySource}
                                            onClick={() => {
                                                if (copySource) {
                                                    copyFrom(copySource)
                                                    setCopySource('')
                                                }
                                            }}
                                        >
                                            <FiCopy size={12} /> Copy
                                        </button>
                                    </div>
                                </div>

                                {priority !== 'Emergency' && (
                                    <div className="notif-tool-row">
                                        <div className="notif-tool-text">
                                            <strong>Apply to all standard priorities</strong>
                                            <span>
                                                Use these {priority} rules for Low, Medium, High and Critical tickets.
                                                Emergency is left unchanged.
                                            </span>
                                        </div>
                                        <div className="notif-tool-actions">
                                            <button type="button" className="btn-ghost btn-sm" onClick={applyToAllStandard}>
                                                Apply to Low–Critical
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {isCustom(priority) && (
                                    <div className="notif-tool-row">
                                        <div className="notif-tool-text">
                                            <strong>Reset {priority} to default</strong>
                                            <span>Undo changes to this priority only. Other priorities are kept.</span>
                                        </div>
                                        <div className="notif-tool-actions">
                                            <button type="button" className="btn-ghost btn-sm" onClick={resetThisPriority}>
                                                <FiRotateCcw size={12} /> Reset
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </section>
                        </>
                    )}
                </div>

                <div className="modal-actions notif-actions">
                    <button className="btn-ghost notif-reset-btn" onClick={handleResetAll} disabled={saving || loading}>
                        <FiRotateCcw size={12} /> Reset all to defaults
                    </button>
                    <div className="notif-actions-right">
                        <button className="btn-ghost" onClick={handleClose}>Close</button>
                        <button className="btn-primary" onClick={handleSave} disabled={saving || loading || !dirty}>
                            <FiSave size={13} />
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ─── Main Component ────────────────────────────────────────────────────────────

type MainTab = 'tickets' | 'updates'

const SupportMain = () => {
    const [mainTab, setMainTab] = useState<MainTab>('tickets')
    const [ticketsTab, setTicketsTab] = useState<TicketsTab>('submitted')
    const [tickets, setTickets] = useState<Ticket[]>([])
    const [updates, setUpdates] = useState<TeamUpdate[]>([])
    const [departments, setDepartments] = useState<DepartmentOption[]>([])
    const [fnnUsers, setFnnUsers] = useState<FnnUser[]>([])
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
    const [loading, setLoading] = useState(false)
    const [filterStatus, setFilterStatus] = useState<string>('All')

    // Modal state
    const [editingTicket, setEditingTicket] = useState<Ticket | null>(null)
    const [deletingTicket, setDeletingTicket] = useState<Ticket | null>(null)
    const [editingUpdate, setEditingUpdate] = useState<TeamUpdate | null | 'new'>(null)
    const [deletingUpdate, setDeletingUpdate] = useState<TeamUpdate | null>(null)
    const [showNotifSettings, setShowNotifSettings] = useState(false)


    // Auth / permissions
    const { isAuthenticated, username, first_name, last_name } = useAuth();
    const [meta, setMeta] = useState<TicketMeta>({ is_manager: false, my_department: null, led_departments: [] })

    const isAdmin = meta.is_manager
    const ledDepartments = meta.led_departments
    const isLeaderOfAny = ledDepartments.length > 0
    const canViewDepartmentTab = isLeaderOfAny || !!meta.my_department

    const manageableDepartments = isAdmin
        ? departments
        : departments.filter(d => ledDepartments.includes(d.name))
    const canManageNotifications = manageableDepartments.length > 0


    useEffect(() => {
        const fetchMeta = async () => {
            try {
                const res = await fetch(apiUrl('/portal/api/support/tickets/meta/'), {
                    method: 'GET',
                    credentials: credentials(),
                })
                if (res.ok) setMeta(await res.json())
            } catch {
                // silently fail
            }
        }
        if (isAuthenticated) fetchMeta()
    }, [isAuthenticated])

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const res = await fetch(apiUrl('/portal/api/support/departments/'), {
                    method: 'GET',
                    credentials: credentials(),
                })
                if (res.ok) setDepartments(await res.json())
            } catch {
                // silently fail
            }
        }
        fetchDepartments()
    }, [])

    useEffect(() => {
        const fetchFnnUsers = async () => {
            try {
                const res = await fetch(apiUrl('/api/inventory/fnn-users/'), {
                    method: 'GET',
                    credentials: credentials(),
                })
                if (res.ok) setFnnUsers(await res.json())
            } catch {
                // silently fail
            }
        }
        fetchFnnUsers()
    }, [])

    const [unreadUpdates, setUnreadUpdates] = useState<number>(0)

    const fetchUnreadUpdates = async () => {
        try {
            const res = await fetch(apiUrl('/portal/api/support/updates/unread/'), {
                method: 'GET',
                credentials: credentials(),
            })
            if (res.ok) {
                const data = await res.json()
                setUnreadUpdates(data.unread_count ?? 0)
            }
        } catch {
            // silently fail
        }
    }

    useEffect(() => { fetchUnreadUpdates() }, [])

    const markUpdatesRead = async () => {
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) return
            await fetch(apiUrl('/portal/api/support/updates/mark-read/'), {
                method: 'POST',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
            })
        } catch {
            // silently fail
        }
    }

    useEffect(() => {
        if (mainTab === 'updates') {
            markUpdatesRead()
            setUnreadUpdates(0)
            window.dispatchEvent(new Event('support-updates-read'))
        }
    }, [mainTab])

    const fetchTickets = async (tab: TicketsTab) => {
        if (tab === 'new') return
        setLoading(true)
        try {
            const response = await fetch(apiUrl(`/portal/api/support/tickets/?view=${tab}`), {
                method: 'GET',
                credentials: credentials(),
            })
            if (response.ok) {
                setTickets(await response.json())
            } else {
                toast.error('Could not load tickets.')
            }
        } catch {
            toast.error('Could not load tickets.')
        } finally {
            setLoading(false)
        }
    }

    const fetchUpdates = async () => {
        try {
            const response = await fetch(apiUrl('/portal/api/support/updates/'), {
                method: 'GET',
                credentials: credentials(),
            })
            if (response.ok) {
                setUpdates(await response.json())
            } else {
                toast.error('Could not load updates.')
            }
        } catch {
            toast.error('Could not load updates.')
        }
    }

    useEffect(() => {
        if (ticketsTab !== 'new') fetchTickets(ticketsTab)
    }, [ticketsTab])

    useEffect(() => { fetchUpdates() }, [])

    const handleDeleteTicket = async () => {
        if (!deletingTicket) return
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            const res = await fetch(apiUrl(`/portal/api/support/tickets/${deletingTicket.ticket_id}/`), {
                method: 'DELETE',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
            })

            if (res.ok) {
                toast.success('Ticket deleted.')
                setDeletingTicket(null)
                if (selectedTicket?.ticket_id === deletingTicket.ticket_id) setSelectedTicket(null)
                fetchTickets(ticketsTab)
            } else {
                toast.error('Failed to delete ticket.')
            }
        } catch {
            toast.error('Failed to delete ticket.')
        }
    }

    const handleDeleteUpdate = async () => {
        if (!deletingUpdate) return
        try {
            const csrfToken = await fetchCsrfToken()
            if (!csrfToken) { toast.error('CSRF error'); return }

            const res = await fetch(apiUrl(`/portal/api/support/updates/${deletingUpdate.update_id}/`), {
                method: 'DELETE',
                credentials: credentials(),
                headers: { 'X-CSRFToken': csrfToken },
            })

            if (res.ok) {
                toast.success('Update deleted.')
                setDeletingUpdate(null)
                fetchUpdates()
            } else {
                toast.error('Failed to delete update.')
            }
        } catch {
            toast.error('Failed to delete update.')
        }
    }

    const filteredTickets = filterStatus === 'All'
        ? tickets
        : tickets.filter(t => t.status === filterStatus)

    // Role flags for whichever ticket is currently being edited
    const editingRoles = editingTicket ? {
        isOwner: nameMatches(editingTicket.created_by_name, username ?? '', first_name ?? '', last_name ?? ''),
        isAssignee: nameMatches(editingTicket.assigned_to_name, username ?? '', first_name ?? '', last_name ?? ''),
        isLeader: !!editingTicket.target_department_name && ledDepartments.includes(editingTicket.target_department_name),
        // Plain department membership (not leader) — only matters for
        // Emergency tickets, where any team member can claim/assign it.
        isDepartmentMember: !!editingTicket.target_department_name && editingTicket.target_department_name === meta.my_department,
    } : null

    return (
        <div className="customer-directory-body">
            <ToastContainer position="bottom-right" />
            <TeamHeader title="Support" />

            <div className="support-container">

                <div className="support-page-header">
                    <div>
                        <h1 className="support-page-title">Ticketing & Updates</h1>
                        <p className="support-page-sub">
                            Report issues, track progress, and stay informed.
                        </p>
                    </div>

                    <div className="support-header-actions">
                        {canManageNotifications && (
                            <button className="btn-ghost notif-settings-btn" onClick={() => setShowNotifSettings(true)}>
                                <FiBell size={13} /> Notifications
                            </button>
                        )}

                        <TelegramConnect />

                        <div className="main-tabs">
                            <button
                                className={`main-tab ${mainTab === 'tickets' ? 'active' : ''}`}
                                onClick={() => setMainTab('tickets')}
                            >
                                <span className="tab-icon">⊟</span>
                                Tickets
                            </button>

                            <button
                                className={`main-tab ${mainTab === 'updates' ? 'active' : ''}`}
                                onClick={() => setMainTab('updates')}
                            >
                                <span className="tab-icon">◈</span>
                                <span className="main-tab-label">Team Updates</span>
                                <span className="main-tab-count">{updates.length}</span>
                                {unreadUpdates > 0 && <span className="main-tab-notification-dot" />}
                            </button>
                        </div>
                    </div>
                </div>

                {mainTab === 'tickets' && (
                    <div className="tickets-layout">
                        <div className={`tickets-panel ${selectedTicket ? 'with-detail' : ''}`}>

                            <div className="tickets-toolbar">
                                <div className="tickets-subtabs">
                                    <button
                                        className={`sub-tab ${ticketsTab === 'submitted' ? 'active' : ''}`}
                                        onClick={() => setTicketsTab('submitted')}
                                    >
                                        Created by Me
                                    </button>
                                    <button
                                        className={`sub-tab ${ticketsTab === 'assigned' ? 'active' : ''}`}
                                        onClick={() => setTicketsTab('assigned')}
                                    >
                                        Assigned to Me
                                    </button>
                                    {canViewDepartmentTab && (
                                        <button
                                            className={`sub-tab ${ticketsTab === 'department' ? 'active' : ''}`}
                                            onClick={() => setTicketsTab('department')}
                                        >
                                            Team Tickets
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <button
                                            className={`sub-tab ${ticketsTab === 'all' ? 'active' : ''}`}
                                            onClick={() => setTicketsTab('all')}
                                        >
                                            All Tickets
                                        </button>
                                    )}
                                    <button
                                        className={`sub-tab ${ticketsTab === 'new' ? 'active' : ''}`}
                                        onClick={() => setTicketsTab('new')}
                                    >
                                        + New
                                    </button>
                                </div>

                                {ticketsTab !== 'new' && (
                                    <div className="status-filters">
                                        {['All', ...STATUSES].map(s => (
                                            <button
                                                key={s}
                                                className={`filter-chip ${filterStatus === s ? 'active' : ''}`}
                                                onClick={() => setFilterStatus(s)}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {ticketsTab !== 'new' ? (
                                <div className="ticket-list">
                                    {loading && <div className="loading-state">Loading tickets…</div>}

                                    {!loading && filteredTickets.length === 0 && (
                                        <div className="empty-state">
                                            No tickets found.
                                            <span className="empty-cta" onClick={() => setTicketsTab('new')}>
                                                Submit one →
                                            </span>
                                        </div>
                                    )}

                                    {filteredTickets.map(t => (
                                        <TicketRow
                                            key={t.ticket_id}
                                            ticket={t}
                                            isAdmin={isAdmin}
                                            ledDepartments={ledDepartments}
                                            myDepartment={meta.my_department}
                                            username={username ?? ''}
                                            first_name={first_name ?? ''}
                                            last_name={last_name ?? ''}
                                            onClick={() => {
                                                setSelectedTicket(prev => prev?.ticket_id === t.ticket_id ? null : t)
                                            }}
                                            onEdit={setEditingTicket}
                                            onDelete={setDeletingTicket}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <NewTicketForm
                                    departments={departments}
                                    onSuccess={() => {
                                        setTicketsTab('submitted')
                                    }}
                                />
                            )}
                        </div>

                        {selectedTicket && (
                            <TicketDetailPanel
                                ticket={selectedTicket}
                                onClose={() => setSelectedTicket(null)}
                            />
                        )}
                    </div>
                )}

                {mainTab === 'updates' && (
                    <UpdatesTab
                        updates={updates}
                        isAdmin={isAdmin}
                        onEdit={setEditingUpdate}
                        onDelete={setDeletingUpdate}
                        onNew={() => setEditingUpdate('new')}
                    />
                )}
            </div>

            {editingTicket && editingRoles && (
                <TicketEditModal
                    ticket={editingTicket}
                    isAdmin={isAdmin}
                    isLeader={editingRoles.isLeader}
                    isAssignee={editingRoles.isAssignee}
                    isOwner={editingRoles.isOwner}
                    isDepartmentMember={editingRoles.isDepartmentMember}
                    departments={departments}
                    fnnUsers={fnnUsers}
                    onClose={() => setEditingTicket(null)}
                    onSaved={() => {
                        fetchTickets(ticketsTab)
                        setSelectedTicket(null)
                    }}
                />
            )}

            {deletingTicket && (
                <ConfirmDeleteModal
                    message={`Delete ticket #${deletingTicket.ticket_id} "${deletingTicket.title}"? This cannot be undone.`}
                    onConfirm={handleDeleteTicket}
                    onCancel={() => setDeletingTicket(null)}
                />
            )}

            {editingUpdate !== null && (
                <UpdateModal
                    update={editingUpdate === 'new' ? null : editingUpdate}
                    onClose={() => setEditingUpdate(null)}
                    onSaved={fetchUpdates}
                />
            )}

            {deletingUpdate && (
                <ConfirmDeleteModal
                    message={`Delete update "${deletingUpdate.title}"? This cannot be undone.`}
                    onConfirm={handleDeleteUpdate}
                    onCancel={() => setDeletingUpdate(null)}
                />
            )}

            {showNotifSettings && (
                <NotificationSettingsModal
                    departments={manageableDepartments}
                    onClose={() => {
                        setShowNotifSettings(false)
                        // member-assignment may have changed → refresh edit permissions on the list
                        if (ticketsTab !== 'new') fetchTickets(ticketsTab)
                    }}
                />
            )}
        </div>
    )
}

export default SupportMain